import { useEffect, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';

/**
 * A modal, built on the browser's own `<dialog>`.
 *
 * Using the native element rather than a div-with-a-fixed-position gets a
 * surprising amount right for free, and all of it matters for staff who are not
 * software people:
 *
 * - **Escape closes it**, without us listening for keys.
 * - **Focus is trapped inside** while it is open, so Tab cannot wander into the
 *   page behind and start filling in fields nobody can see.
 * - **Focus returns to whatever opened it** when it closes, so the counter's
 *   place in the page is not lost.
 * - **The page behind cannot scroll**, which on a phone is the difference
 *   between a form and a wrestling match.
 * - Everything behind is inert, so a stray tap cannot trigger it.
 *
 * Clicking the dark area around the panel closes it, as asked. That is a
 * deliberate trade: it is what everybody expects, and it does mean a half-typed
 * form can be dismissed by a clumsy tap. The panel therefore has generous
 * padding around it, so the closeable area is not immediately beside the
 * fields.
 */

interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Names the modal for screen readers and titles it for everybody else. */
  readonly title: string;
  /** One line under the title, saying what this is for. */
  readonly note?: string;
  /** `wide` is for the booking form, which has real content in it. */
  readonly size?: 'base' | 'wide';
  readonly children: ReactNode;
  /** The buttons. Kept out of the scrolling area so they are always reachable. */
  readonly footer?: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  note,
  size = 'base',
  children,
  footer,
}: ModalProps): ReactElement | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useRef(`modal-${Math.random().toString(36).slice(2, 9)}`).current;

  useEffect(() => {
    const element = dialog.current;
    if (element === null) return;

    if (open && !element.open) {
      element.showModal();

      // showModal() focuses the first focusable thing it finds, which is the
      // Close button — so a modal opens with the cursor on the way out rather
      // than in the field. React's autoFocus does not survive this either: it
      // calls .focus() at mount, before the dialog is showing.
      //
      // So: the first field, if there is one. Somebody who opened a form to
      // type should be able to start typing.
      const field = element.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, select',
      );
      field?.focus();
    }
    if (!open && element.open) element.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialog}
      aria-labelledby={titleId}
      // Fires for Escape and for the form's own close, so both paths end up in
      // the same place rather than only the one we remembered to handle.
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click whose target is the dialog itself landed on the backdrop:
        // anything inside the panel targets the panel or its children.
        if (event.target === dialog.current) onClose();
      }}
      className={[
        'w-[calc(100vw-1.5rem)] max-h-[calc(100dvh-2rem)] rounded border border-rule bg-panel p-0',
        'text-ink backdrop:bg-ink/60',
        size === 'wide' ? 'sm:max-w-3xl' : 'sm:max-w-xl',
      ].join(' ')}
    >
      {/* One flex column: header and footer pinned, middle scrolls. On a phone
          this is what keeps "Save" on screen with the keyboard up. */}
      <div className="flex max-h-[calc(100dvh-2rem)] flex-col">
        <header className="flex items-start gap-3 border-b border-rule border-l-[3px] border-l-signal px-5 py-4 pl-[calc(1.25rem-3px)]">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-heading font-semibold">
              {title}
            </h2>
            {note !== undefined && <p className="mt-0.5 text-small text-ink-muted">{note}</p>}
          </div>

          <button
            type="button"
            onClick={onClose}
            // A real word, not a glyph. "×" is not obvious to everybody, and
            // this is the control someone reaches for when they are unsure.
            className="-mr-2 -mt-1 shrink-0 rounded px-2 py-1 text-small font-medium text-ink-muted hover:bg-paper hover:text-ink"
          >
            Close
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>

        {footer !== undefined && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-rule bg-paper px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  );
}
